import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../Icon';
import '../../styles/components/AttachmentsPanel.css';
import { useConfirm } from './ConfirmProvider';
import {
  getAttachments,
  uploadImage,
  deleteAttachment,
  restoreAttachment,
  deleteAttachmentPermanently,
  resolveMediaUrl,
  type Attachment,
} from '../../services/uploadService';

/**
 * Attachments for one entity: upload, open, archive, restore, and (only for
 * something already archived) permanently delete.
 *
 * Shared because the procurement order and quote pages had each grown their
 * own copy, and because attachments are the documentary record behind the
 * money — signed contracts, official invoices, payment receipts. Archiving is
 * the default destructive action; the file survives on disk. Permanent
 * deletion is deliberately a second step taken from the archived list, and
 * the server enforces that ordering too rather than trusting this UI.
 */
export interface AttachmentsPanelProps {
  entityType: string;
  entityId: string | undefined;
  title?: string;
  uploadLabel?: string;
  /** Shown instead of the uploader before the parent record exists. */
  disabledMessage?: string;
  /** Locks the panel: files stay listable and openable, but nothing can be
   *  added, archived or deleted. For records that are no longer editable —
   *  an accepted or rejected quote is the frozen documentary record of what
   *  was bid, so its attachments shouldn't change either. */
  readOnly?: boolean;
  /** Explains why the panel is locked. */
  readOnlyMessage?: string;
  onError?: (message: string) => void;
}

export default function AttachmentsPanel({
  entityType,
  entityId,
  title = 'Invoices & Attachments',
  uploadLabel = '+ Attach invoice or receipt',
  disabledMessage = 'Save first to attach files.',
  readOnly = false,
  readOnlyMessage,
  onError,
}: AttachmentsPanelProps) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [archived, setArchived] = useState<Attachment[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();

  const fail = useCallback((msg: string) => {
    if (onError) onError(msg); else window.alert(msg);
  }, [onError]);

  const load = useCallback(async () => {
    if (!entityId) { setItems([]); setArchived([]); return; }
    try {
      const [live, gone] = await Promise.all([
        getAttachments(entityType, entityId),
        getAttachments(entityType, entityId, true),
      ]);
      setItems(live);
      setArchived(gone);
    } catch {
      setItems([]); setArchived([]);
    }
  }, [entityType, entityId]);

  useEffect(() => { void load(); }, [load]);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || !entityId) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadImage(file, { entityType, entityId, label: file.name });
      }
      await load();
    } catch {
      fail('Failed to upload attachment.');
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const archive = async (att: Attachment) => {
    if (!(await confirm({
      title: 'Archive this attachment?',
      description: 'It stays recoverable — it is not deleted from the server.',
      confirmLabel: 'Archive',
    }))) return;
    try { await deleteAttachment(att.id); await load(); }
    catch { fail('Failed to archive attachment.'); }
  };

  const restore = async (att: Attachment) => {
    try { await restoreAttachment(att.id); await load(); }
    catch { fail('Failed to restore attachment.'); }
  };

  const destroy = async (att: Attachment) => {
    const name = att.label || att.original_name || 'this attachment';
    if (!(await confirm({
      title: `Permanently delete ${name}?`,
      description: 'The file is removed from the server and cannot be recovered.',
      confirmLabel: 'Delete forever',
    }))) return;
    try { await deleteAttachmentPermanently(att.id); await load(); }
    catch (err) { fail(err instanceof Error ? err.message : 'Failed to delete attachment.'); }
  };

  const row = (att: Attachment, isArchived: boolean) => (
    <li key={att.id} className="rpf-attachment-item">
      <a
        href={resolveMediaUrl(att.url)}
        target="_blank"
        rel="noopener noreferrer"
        className="rpf-attachment-link"
        title={att.label ?? 'View attachment'}
      >
        {att.mime_type?.startsWith('image/') ? (
          <img
            className="rpf-attachment-thumb"
            src={resolveMediaUrl(att.url)}
            alt={att.label || att.original_name || 'Photo'}
            loading="lazy"
          />
        ) : (
          <Icon name="document" size={14} />
        )}
        <span>{att.label || att.original_name || 'Attachment'}</span>
      </a>
      {readOnly ? null : isArchived ? (
        <>
          <button type="button" className="rpf-attachment-remove" onClick={() => { void restore(att); }} title="Restore">
            <Icon name="refresh" size={14} />
          </button>
          <button type="button" className="rpf-attachment-remove" onClick={() => { void destroy(att); }} title="Delete permanently">
            <Icon name="trash" size={14} />
          </button>
        </>
      ) : (
        <button type="button" className="rpf-attachment-remove" onClick={() => { void archive(att); }} aria-label="Archive attachment">
          <Icon name="x" size={14} />
        </button>
      )}
    </li>
  );

  return (
    <section className="side-section">
      <h4>{title}</h4>
      {!entityId ? (
        <p className="poe-label-hint">{disabledMessage}</p>
      ) : (
        <>
          {items.length > 0 && <ul className="rpf-attachment-list">{items.map(a => row(a, false))}</ul>}
          {readOnly ? (
            (readOnlyMessage || items.length === 0) && (
              <p className="poe-label-hint">
                {readOnlyMessage ?? 'No attachments.'}
              </p>
            )
          ) : (
          <>
          <label
            className={`sidebar-photo-upload${items.length > 0 ? ' sidebar-photo-upload--mt' : ''}`}
            onClick={e => { e.preventDefault(); inputRef.current?.click(); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); void handleFiles(e.dataTransfer.files); }}
          >
            <span className="sidebar-photo-upload-btn">{uploading ? 'Uploading…' : uploadLabel}</span>
          </label>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            multiple
            className="u-hidden"
            onChange={e => { void handleFiles(e.target.files); }}
          />
          </>
          )}

          {archived.length > 0 && (
            <>
              <button
                type="button"
                className="pt-archived-toggle ap-archived-toggle"
                onClick={() => setShowArchived(v => !v)}
                aria-expanded={showArchived}
              >
                <Icon name="folder" size={14} />
                {showArchived ? 'Hide archived' : `Archived (${archived.length})`}
              </button>
              {showArchived && <ul className="rpf-attachment-list">{archived.map(a => row(a, true))}</ul>}
            </>
          )}
        </>
      )}
    </section>
  );
}
